import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next"; 
import GenericPersona from "../../GenericComponents/PersonComponent";
import { Tooltip } from "@fluentui/react-components";
import { getAccessToken, getAgentPhoto } from "../../../Services/GraphServices";
import {
  getAgentConnectedCalls,
  getAgentInformation,
  getMyAgentProfile,
} from "../../../Services/HeedifyServices";
import "./CSS/AgentComponent.css";

const AgentComponent = ({
  agent,
  agentId,
  agentPresences,
  signalRConnection,
}) => {
  const [agentData, setAgentData] = useState({
    photoUrl: null,
    connectedCalls: 0,
    totalCalls: 0,
    missedCalls: 0,
    totalCallsDuration: 0,
  });
  const [timezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone // Par défaut : fuseau détecté automatiquement
  );
   const { t } = useTranslation(); 
  const authContext = useMsal();
  const { accounts, instance } = authContext;
  const activeAccount = accounts[0];
  const userId = activeAccount.localAccountId;
  const tenantId = activeAccount.tenantId;
  // Récupérer la présence de l'agent
  const agentPresence = agentPresences?.[agentId];
  const availability = agentPresence?.availability;
  
  //récupération timezone
  const getNumericTimezone = (ianaTimezone) => {
    const offsetMinutes = moment.tz(ianaTimezone).utcOffset(); // Décalage en minutes
    return Math.floor(offsetMinutes / 60); // Convertir en heures
  };
  const numericTimezone = getNumericTimezone(timezone);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };
  // Fonction pour récupérer la photo de l'agent
  const fetchAgentPhoto = () => {
    getAccessToken(instance, activeAccount)
      .then((accessToken) => getAgentPhoto(agentId, accessToken))
      .then((photo) => {
        setAgentData((prevDetails) => ({
          ...prevDetails,
          photoUrl: photo || null,
        }));
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération de la photo de l'agent :",
          error
        );
        setAgentData((prevDetails) => ({ ...prevDetails, photoUrl: null }));
      });
  };

  // Fonction pour récupérer les appels connectés
  const fetchConnectedCalls = () => {
    getAccessToken(instance, activeAccount)
      .then((token) =>
        getAgentConnectedCalls(tenantId, agentId, token).then((calls) => {
          const connectedCallsLength = calls?.connectedCalls
            ? Object.keys(calls.connectedCalls).length
            : 0;
          setAgentData((prevDetails) => ({
            ...prevDetails,
            connectedCalls: connectedCallsLength,
          }));
        })
      )
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération des appels connectés :",
          error
        );
      });
  };

  // Fonction pour récupérer les informations de l'agent
  const fetchAgentInformation = () => {
    getAccessToken(instance, activeAccount)
      .then((token) =>
        getMyAgentProfile(tenantId, userId, token).then((profile) =>
          getAgentInformation(
            tenantId,
            profile.scope,
            agentId,
            numericTimezone,
            true
          ).then((info) => {
           
            setAgentData((prevDetails) => ({
              ...prevDetails,
              totalCalls: info?.totalCalls || 0,
              missedCalls: info?.missedCalls || 0,
              totalCallsDuration: info?.totalCallsDuration || 0,
            }));
          })
        )
      )
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération des informations de l'agent :",
          error
        );
      });
  };

  useEffect(() => {
    if (agentId) {
      fetchAgentPhoto();
      fetchConnectedCalls();
      fetchAgentInformation();
    }
    if (signalRConnection) {
      const connectedCallsEvent = `agentCalls${agentId}`;
      const performanceEvent = `agentPerformance${agentId}`;

      // Event pour les connectedCalls
      const onConnectedCallsReceived = (result) => {
     
        if (result?.connectedCalls) {
          const connectedCallsLength = Object.keys(
            result.connectedCalls
          ).length;
          setAgentData((prevDetails) => ({
            ...prevDetails,
            connectedCalls: connectedCallsLength,
          }));
        }
      };

      // Event pour les informations d'agent et gestion de la logique des appels
      const onPerformanceDataReceived = (result) => {
     

        setAgentData((prevDetails) => {
          if (result?.call) {
            const { isMissed, duration } = result.call;
            return {
              ...prevDetails,
              totalCalls: (prevDetails.totalCalls || 0) + 1,
              missedCalls: isMissed
                ? (prevDetails.missedCalls || 0) + 1
                : prevDetails.missedCalls,
              totalCallsDuration:
                (prevDetails.totalCallsDuration || 0) + (duration || 0),
            };
          }
        });
      };

      // Écouter les événements SignalR 
      signalRConnection.on(connectedCallsEvent, onConnectedCallsReceived);
      signalRConnection.on(performanceEvent, onPerformanceDataReceived);
    
    }
  }, [signalRConnection, agentId]);

  return (
    <>
      <Tooltip withArrow content={agent.displayName} relationship="label">
        <div style={{ width: "30%" }}>
          <GenericPersona
            name={agent.displayName}
            secondaryText={t(`PRESENCE.${availability}`)}
            className="agent-detail-value"
            availability={availability}
            avatar={{
              image: {
                src: agentData.photoUrl,
              },
            }}
          />
        </div>
      </Tooltip>
      <span className="agent-detail-value"  >{agentData.connectedCalls} </span>
      <span className="agent-detail-value missed-calls-margin"  >{agentData.missedCalls}</span>
     
      <span className="agent-detail-value"  >{agentData.totalCalls}</span>
      <span className="agent-detail-value">
        {formatTime(agentData.totalCallsDuration)}
      </span>
    </>
  );
};

export default AgentComponent;
